import { useState } from "react";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { shortUuid } from "@egalregal/shared";
import { useT } from "../i18n";
import { CheckIcon, CopyIcon, QrCodeIcon } from "./icons";
import { LabelPreviewDialog } from "./LabelPreviewDialog";
import { ShortUuidIcons } from "./ShortUuidIcons";

interface IdentityLineProps {
  uuid: string;
  /** Human-readable name for the QR label; falls back to the short UUID. */
  name?: string;
  /** Optional confirmation surface (e.g. a snackbar); falls back to an inline check. */
  onCopied?: (message: string) => void;
  /** Hide the QR action for an entity without a printable label. */
  showQr?: boolean;
}

/**
 * The one identity line (ADR-0027): four short-UUID pictograms, the short UUID,
 * a copy action and — since 2026-10-28 — a QR action that opens the label
 * preview dialog. Used by container detail, scanner summaries, item scans and
 * search levels 2/3, so the identity always reads and behaves the same.
 *
 * The QR encodes the full UUID and the label shows the given `name`, matching
 * the printed label. The copy button copies the full UUID; the pictograms are
 * decorative.
 */
export function IdentityLine({ uuid, name, onCopied, showQr = true }: IdentityLineProps) {
  const t = useT();
  const [copied, setCopied] = useState(false);
  const [labelOpen, setLabelOpen] = useState(false);

  const copyUuid = () => {
    if (!navigator.clipboard) return;
    void navigator.clipboard
      .writeText(uuid)
      .then(() => {
        setCopied(true);
        onCopied?.(t("uuidCopied"));
        window.setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => undefined);
  };

  return (
    <Stack direction="row" alignItems="center" spacing={0.5} data-testid="identity-line">
      <ShortUuidIcons uuid={uuid} />
      <Typography
        variant="caption"
        color="text.secondary"
        data-testid="container-uuid"
        sx={{ fontFamily: "monospace" }}
      >
        {shortUuid(uuid)}
      </Typography>
      <Tooltip title={t("copyUuid")}>
        <IconButton size="small" aria-label={t("copyUuid")} onClick={copyUuid}>
          {copied ? <CheckIcon fontSize="inherit" /> : <CopyIcon fontSize="inherit" />}
        </IconButton>
      </Tooltip>
      {showQr && (
        <Tooltip title={t("showLabelPreview")}>
          <IconButton
            size="small"
            aria-label={t("showLabelPreview")}
            data-testid="identity-qr"
            onClick={() => setLabelOpen(true)}
          >
            <QrCodeIcon fontSize="inherit" />
          </IconButton>
        </Tooltip>
      )}
      {copied && !onCopied && (
        <Typography
          component="span"
          role="status"
          data-testid="identity-copy-status"
          sx={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0 0 0 0)" }}
        >
          {t("uuidCopied")}
        </Typography>
      )}
      {showQr && (
        <LabelPreviewDialog
          open={labelOpen}
          onClose={() => setLabelOpen(false)}
          uuid={uuid}
          name={name ?? shortUuid(uuid)}
        />
      )}
    </Stack>
  );
}
